import type { StatusTone } from "../filters/statusColors";
import {
  Activity,
  AlarmClock,
  AlignLeft,
  BadgeCheck,
  Boxes,
  Building2,
  CalendarClock,
  CalendarDays,
  CircleDot,
  ClipboardList,
  File as FileIcon,
  FileBadge,
  FileSpreadsheet,
  FileText,
  Film,
  Image as ImageIcon,
  Music,
  Presentation,
  Globe,
  Hash,
  IndianRupee,
  Landmark,
  Layers,
  Link2,
  Mail,
  MapPin,
  Network,
  Phone,
  Repeat,
  Scale,
  Shapes,
  ShieldCheck,
  StickyNote,
  Tag,
  Timer,
  UserCog,
  UserRound,
  Users,
  type LucideIcon,
} from "lucide-react";
import { usePreview } from "../preview/PreviewContext";

/* Types and helpers behind the card pieces in cardParts.tsx. */

/** One line of a panel. The first line of a panel reads as its headline. */
export type CardPanelLine = {
  text: string;
  /** Codes, ids and numbers: monospaced so they are easy to compare. */
  mono?: boolean;
  /** Up to this many lines before truncating (default 1). */
  lines?: 1 | 2 | 3;
  /** Offer a copy button beside the value (an email, a code). */
  copy?: boolean;
};

/** A titled group of related facts — "Location", "Contact", … */
export type CardPanel = {
  icon: LucideIcon;
  title: string;
  lines?: CardPanelLine[];
  /** Small label/value pairs laid out two to a row. */
  stats?: { label: string; value: string }[];
  chips?: string[];
  /** Shows the chips as toned pills with this icon (roles, say), every one
   *  visible, instead of the quiet accent chips folded into "+N". */
  chipTone?: { tone: StatusTone; toneIcon?: LucideIcon };
  /** Take the whole row rather than half of it. */
  wide?: boolean;
};

/** Optional richer layout for a row: a page that sets `card` on its items
 *  gets grouped panels instead of the flat `meta` tiles. */
export type CardLayout = {
  /** Under the title: a code or id, then what kind of record this is. */
  code?: string;
  kind?: string;
  /** The one number worth seeing at a glance, top right. */
  stat?: { value: string | number; label: string };
  /** Chips after the status — "Default", "Individual", … */
  chips?: string[];
  panels: CardPanel[];
};

export const EMPTY = "-";
export const hasText = (value?: string | null): value is string =>
  !!value && value.trim() !== "" && value.trim() !== EMPTY;

/** Surface shared by grouped panels and plain meta tiles. */
// A column, with the value surface below filling it: two panels side by side
// are the same height (grid rows stretch), and their white value boxes now
// match too instead of one ending short of its neighbour.
export const PANEL_CLASS =
  "flex min-w-0 flex-col rounded-xl border bg-[rgb(var(--card-muted))] px-3 py-2.5 transition-colors";

/** A hue per card. Stepping round the wheel by the golden angle puts every
 *  card a long way from its neighbours — hashing the title instead gave two
 *  blues side by side as often as not. Without a position, fall back to a
 *  hash of the seed. */
function hueFor(seed: string, index?: number) {
  if (typeof index === "number") return (index * 137.508 + 168) % 360;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1)
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash) % 360;
}

/** The accent gradient of a record — its card banner, list strip and table
 *  row edge all use it. */
export const bannerGradient = (seed: string, index?: number) => {
  const hue = Math.round(hueFor(seed, index));
  return [
    `radial-gradient(circle at 18% 120%, hsl(${(hue + 40) % 360} 70% 60% / 0.55), transparent 55%)`,
    `radial-gradient(circle at 85% -10%, hsl(${(hue + 320) % 360} 75% 65% / 0.5), transparent 50%)`,
    `linear-gradient(120deg, hsl(${hue} 55% 42%), hsl(${(hue + 50) % 360} 60% 52%))`,
  ].join(",");
};

/** The lighter surface a tile's value sits on, inside its muted ground. */
export const VALUE_SURFACE =
  "value-surface mt-1.5 min-w-0 flex-1 rounded-lg bg-[rgb(var(--card))] px-2 py-1";

/** Opens a record's photo in the preview modal. */
export function useOpenPhoto() {
  const { openPreview } = usePreview();
  return (src?: string, name?: string) => {
    if (!src) return;
    openPreview([{ url: src, name: name || "Photo", type: "image/jpeg" }], 0);
  };
}

/**
 * The icon that leads a tile's label, picked from what the field is about.
 * Matched on the field's key and label together, first hit wins — so the
 * specific entries ("signing authority", "business unit") sit above the
 * general ones ("unit", "name"). A row can still name its own with `icon`.
 */
const LABEL_ICONS: [RegExp, LucideIcon][] = [
  [/e-?mail/, Mail],
  [/phone|mobile|contact number/, Phone],
  [/website|domain|url/, Globe],
  [/address|location|city|state|locality|country|pin ?code|region/, MapPin],
  [/renew|remind/, AlarmClock],
  [/expir|due|deadline/, CalendarClock],
  [/date|created|updated|start|end\b|period|since/, CalendarDays],
  [/term|duration|frequency|time\b/, Timer],
  [/recurr|occurr|cycle/, Repeat],
  [
    /value|amount|price|outstanding|recover|invoice|fee|cost|penalt/,
    IndianRupee,
  ],
  [/caf|approv|verified|complian/, BadgeCheck],
  [/status|stage|state of|progress|health/, Activity],
  [/connected|linked|parent|agreement|reference/, Link2],
  [/clause|obligation/, Scale],
  [/gst|pan\b|registration|reg\.|licen[cs]e|tax|aadhaar/, FileBadge],
  [/number|\bno\.?\b|code|\bid\b|job order|order/, Hash],
  [/business unit|organi[sz]ation|company|entity name|brand/, Building2],
  [/department|team|division/, Network],
  [/role|permission|scope|access/, ShieldCheck],
  [/manager|reporting|supervisor/, UserCog],
  [/part(y|ies)|members?|users?|staff|debtor|customer/, Users],
  [
    /authorit|signator|assignee|requester|initiator|editor|reviewer|approver|contact|person|poc|owner|father|name/,
    UserRound,
  ],
  [/categor|tag/, Tag],
  [/type|kind|class/, Shapes],
  [/module|plan|subscription|package/, Layers],
  [/template|document|file|content/, FileText],
  [/bank|account/, Landmark],
  [/description|remark|note|reason|summary|purpose|bio/, StickyNote],
  [/count|total|quantity|documents|fields|items/, Boxes],
  [/task|action|job/, ClipboardList],
  [/text|message|body/, AlignLeft],
];

export function iconForField(label?: string, key?: string): LucideIcon {
  const text = `${key ?? ""} ${label ?? ""}`
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase();
  return LABEL_ICONS.find(([pattern]) => pattern.test(text))?.[1] ?? CircleDot;
}

/** Lower-case extension of a file name, "" when it has none. */
export const extensionOf = (name?: string) =>
  name?.includes(".") ? name.split(".").pop()!.toLowerCase() : "";

/** The icon a file is shown with wherever it appears as a chip or tile (card
 *  file rows, the preview's strip): by MIME type first, then extension. */
export const fileIconFor = (file: {
  name?: string;
  type?: string;
}): LucideIcon => {
  const type = file.type ?? "";
  const ext = extensionOf(file.name);
  if (["png", "jpg", "jpeg", "webp", "gif", "bmp", "svg"].includes(ext))
    return ImageIcon;
  if (["mp4", "mov", "webm", "avi", "mkv"].includes(ext)) return Film;
  if (["mp3", "wav", "m4a", "ogg", "aac"].includes(ext)) return Music;
  if (["xls", "xlsx", "csv"].includes(ext)) return FileSpreadsheet;
  if (["ppt", "pptx"].includes(ext)) return Presentation;
  if (["pdf", "doc", "docx", "txt"].includes(ext)) return FileText;
  if (type.startsWith("image")) return ImageIcon;
  if (type.startsWith("video")) return Film;
  if (type.startsWith("audio")) return Music;
  return FileIcon;
};
